import { useEffect, useMemo, useState } from 'react';
import FilePreviewModal from '../components/health/FilePreviewModal';
import MetricChartCard from '../components/health/MetricChartCard';
import { pickLocalized, useLang, useT } from '../i18n';
import { api } from '../lib/api';
import type {
  HealthCategoryKey,
  HealthFindingStatus,
  HealthMetricTrend,
  HealthRecord,
} from '../types/health';

type Status = 'loading' | 'ready' | 'error';
type CategoryFilter = 'all' | HealthCategoryKey;

const categoryOrder: HealthCategoryKey[] = [
  'blood',
  'thyroid',
  'metabolic',
  'inflammation',
  'ct',
  'ultrasound',
  'endoscopy',
  'other',
];

const statusClasses: Record<HealthFindingStatus, string> = {
  normal: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  abnormal: 'border-amber-200 bg-amber-50 text-amber-700',
  high: 'border-rose-200 bg-rose-50 text-rose-700',
  low: 'border-sky-200 bg-sky-50 text-sky-700',
  unknown: 'border-neutral-200 bg-neutral-50 text-coffee',
};

export default function Health() {
  const t = useT();
  const { lang } = useLang();
  const [status, setStatus] = useState<Status>('loading');
  const [records, setRecords] = useState<HealthRecord[]>([]);
  const [trends, setTrends] = useState<HealthMetricTrend[]>([]);
  const [error, setError] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('all');
  const [previewPath, setPreviewPath] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.healthRecords.list(), api.healthRecords.trends()])
      .then(([recordData, trendData]) => {
        if (cancelled) return;
        setRecords(recordData);
        setTrends(trendData.series);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'unknown error');
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedRecords = useMemo(
    () => [...records].sort((a, b) => recordTime(b) - recordTime(a)),
    [records],
  );

  const formatter = useMemo(
    () => new Intl.DateTimeFormat(lang === 'zh' ? 'zh-CN' : 'en-US', { dateStyle: 'medium' }),
    [lang],
  );

  const groupedRecords = useMemo(() => groupRecordsByCategory(sortedRecords), [sortedRecords]);
  const filteredRecords = useMemo(
    () => selectedCategory === 'all'
      ? sortedRecords
      : sortedRecords.filter((record) => getRecordCategories(record).includes(selectedCategory)),
    [selectedCategory, sortedRecords],
  );
  const dateRange = useMemo(() => getDateRange(sortedRecords, formatter, t('health.dateUnknown')), [formatter, sortedRecords, t]);
  const latestRecord = sortedRecords[0];
  const activeCategories = categoryOrder.filter((category) => groupedRecords[category].length > 0);

  return (
    <section className="flex flex-col gap-10">
      <header className="max-w-4xl flex flex-col gap-3">
        <p className="font-mono text-xs uppercase tracking-[0.24em] text-coffee">
          {t('health.kicker')}
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:gap-3">
          <h2 className="text-3xl md:text-5xl font-semibold text-cocoa leading-tight">
            {t('health.title')}
          </h2>
          <span className="font-mono text-xs text-coffee/70">{t('health.apiHint')}</span>
        </div>
        <p className="text-coffee leading-relaxed">{t('health.subtitle')}</p>
      </header>

      <div className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-4 text-sm leading-relaxed text-coffee shadow-sm">
        {t('health.privacyHint')}
      </div>

      {status === 'loading' && <p className="text-coffee">{t('health.loading')}</p>}
      {status === 'error' && (
        <p className="text-cocoa font-mono text-sm italic">{t('health.error', { error })}</p>
      )}

      {status === 'ready' && (
        <div className="flex flex-col gap-10 animate-fade-in">
          {sortedRecords.length === 0 ? (
            <article className="rounded-xl border border-neutral-200 bg-white p-6 shadow-soft transition-shadow duration-200 hover:shadow-float">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-coffee">
                {t('health.timelineLabel')}
              </p>
              <p className="mt-3 text-coffee">{t('health.empty')}</p>
            </article>
          ) : (
            <>
              <section>
                <SectionHeading label={t('health.overview.title')} />
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <StatCard label={t('health.overview.records')} value={String(sortedRecords.length)} />
                  <StatCard label={t('health.overview.dateRange')} value={dateRange} />
                  <StatCard
                    label={t('health.overview.latest')}
                    value={latestRecord ? formatRecordDate(latestRecord, formatter, t('health.dateUnknown')) : t('health.dateUnknown')}
                  />
                  <StatCard label={t('health.overview.categories')} value={String(activeCategories.length)} />
                </div>
              </section>

              <section className="flex flex-col gap-3">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-coffee/70">
                  {t('health.filterLabel')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <CategoryButton
                    label={t('health.allCategories')}
                    count={sortedRecords.length}
                    active={selectedCategory === 'all'}
                    onClick={() => setSelectedCategory('all')}
                  />
                  {categoryOrder.map((category) => (
                    <CategoryButton
                      key={category}
                      label={t(`health.categories.${category}`)}
                      count={groupedRecords[category].length}
                      active={selectedCategory === category}
                      onClick={() => setSelectedCategory(category)}
                    />
                  ))}
                </div>
              </section>

              <section>
                <SectionHeading label={t('health.sections.charts')} />
                {trends.length === 0 ? (
                  <p className="mt-4 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-5 text-coffee">
                    {t('health.chart.noData')}
                  </p>
                ) : (
                  <div className="mt-4 grid gap-4 lg:grid-cols-2">
                    {trends.map((series) => (
                      <MetricChartCard key={series.key} series={series} />
                    ))}
                  </div>
                )}
              </section>

              <section>
                <SectionHeading label={t('health.sections.categories')} />
                <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {categoryOrder.map((category) => (
                    <CategorySummary
                      key={category}
                      category={category}
                      records={groupedRecords[category]}
                      formatter={formatter}
                      selected={selectedCategory === category}
                      onSelect={() => setSelectedCategory(category)}
                    />
                  ))}
                </div>
              </section>

              <section>
                <SectionHeading label={t('health.sections.timeline')} />
                <Timeline records={filteredRecords} formatter={formatter} onPreviewSource={setPreviewPath} />
              </section>
            </>
          )}
        </div>
      )}
      <FilePreviewModal filePath={previewPath} onClose={() => setPreviewPath(null)} />
    </section>
  );
}

function SectionHeading({ label }: { label: string }) {
  return (
    <h3 className="font-mono text-xs uppercase tracking-[0.24em] text-coffee">
      {label}
    </h3>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <article className="rounded-xl border border-neutral-200 bg-white p-4 shadow-soft">
      <p className="font-mono text-[10px] uppercase tracking-wider text-coffee/70">{label}</p>
      <p className="mt-2 text-xl font-semibold text-cocoa">{value}</p>
    </article>
  );
}

function CategoryButton({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'rounded-full border px-3 py-1.5 font-mono text-xs shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15',
        active
          ? 'border-[rgb(240_240_240)] bg-tan text-neutral-100'
          : 'border-neutral-200 bg-white text-coffee hover:-translate-y-0.5 hover:border-neutral-300 hover:text-cocoa hover:shadow-soft',
      ].join(' ')}
    >
      {label} · {count}
    </button>
  );
}

function CategorySummary({
  category,
  records,
  formatter,
  selected,
  onSelect,
}: {
  category: HealthCategoryKey;
  records: HealthRecord[];
  formatter: Intl.DateTimeFormat;
  selected: boolean;
  onSelect: () => void;
}) {
  const t = useT();
  const { lang } = useLang();
  const latest = records[0];
  const preview = records.slice(0, 3);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        'rounded-xl border bg-white p-5 text-left shadow-soft transition hover:-translate-y-0.5 hover:shadow-float focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15',
        selected ? 'border-[rgb(240_240_240)]' : 'border-neutral-200',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-coffee/70">
            {t('health.categoryCount', { count: records.length })}
          </p>
          <h4 className="mt-1 text-xl font-semibold text-cocoa">
            {t(`health.categories.${category}`)}
          </h4>
        </div>
        {latest && (
          <span className="rounded-full border border-neutral-200 bg-neutral-50 px-2 py-1 font-mono text-[10px] text-coffee/70">
            {t('health.categoryLatest', { date: formatRecordDate(latest, formatter, t('health.dateUnknown')) })}
          </span>
        )}
      </div>
      {preview.length > 0 ? (
        <ul className="mt-4 flex flex-col gap-2">
          {preview.map((record) => (
            <li key={record.id} className="text-sm text-coffee">
              <span className="font-medium text-cocoa">{pickLocalized(record.title, lang)}</span>
              <span className="ml-2 font-mono text-[10px] text-coffee/60">
                {formatRecordDate(record, formatter, t('health.dateUnknown'))}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 text-sm text-coffee/60">{t('health.empty')}</p>
      )}
    </button>
  );
}

function Timeline({
  records,
  formatter,
  onPreviewSource,
}: {
  records: HealthRecord[];
  formatter: Intl.DateTimeFormat;
  onPreviewSource: (path: string) => void;
}) {
  const t = useT();
  const { lang } = useLang();

  if (records.length === 0) {
    return (
      <p className="mt-4 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-5 text-coffee">
        {t('health.empty')}
      </p>
    );
  }

  return (
    <ol className="mt-4 relative flex flex-col gap-5 border-l border-neutral-200 pl-5">
      {records.map((record) => (
        <li key={record.id} className="relative">
          <span className="absolute -left-[29px] top-6 h-3 w-3 rounded-full border-2 border-white bg-tan shadow-soft" />
          <article className="rounded-xl border border-neutral-200 bg-white p-5 shadow-soft transition-shadow duration-200 hover:shadow-float">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-coffee/70">
                  {formatRecordDate(record, formatter, t('health.dateUnknown'))}
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-cocoa">
                  {pickLocalized(record.title, lang)}
                </h3>
              </div>
              <span className="w-fit rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 font-mono text-xs text-coffee">
                {t(`health.kind.${record.kind}`)}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap gap-2 text-xs font-mono text-coffee/80">
              {record.provider && <span>{record.provider}</span>}
              {record.department && <span>· {record.department}</span>}
              <span>· {t('health.parsedAtLabel')} {formatDate(record.parsedAt, formatter, t('health.dateUnknown'))}</span>
            </div>

            <section className="mt-5">
              <p className="font-mono text-[10px] uppercase tracking-wider text-coffee">
                {t('health.summaryLabel')}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-coffee">
                {pickLocalized(record.summary, lang)}
              </p>
            </section>

            {record.findings.length > 0 && (
              <section className="mt-5">
                <p className="font-mono text-[10px] uppercase tracking-wider text-coffee">
                  {t('health.findingsLabel')}
                </p>
                <ul className="mt-3 grid gap-2 md:grid-cols-2">
                  {record.findings.map((finding, index) => {
                    const statusKey = finding.status ?? 'unknown';
                    return (
                      <li
                        key={`${record.id}-${index}-${pickLocalized(finding.label, lang)}`}
                        className="rounded-lg border border-neutral-200 bg-neutral-50 p-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-sm font-medium text-cocoa">
                            {pickLocalized(finding.label, lang)}
                          </span>
                          <span
                            className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${statusClasses[statusKey]}`}
                          >
                            {t(`health.status.${statusKey}`)}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-coffee">
                          <span className="font-semibold text-cocoa">{finding.value}</span>
                          {finding.unit ? ` ${finding.unit}` : ''}
                        </p>
                        {finding.referenceRange && (
                          <p className="mt-1 text-xs text-coffee/70">
                            {t('health.referenceRangeLabel')} {finding.referenceRange}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {record.notes && (
              <p className="mt-5 rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-3 text-xs leading-relaxed text-coffee">
                {record.notes}
              </p>
            )}

            {(record.tags.length > 0 || sourcePaths(record).length > 0) && (
              <footer className="mt-5 flex flex-col gap-3 border-t border-neutral-100 pt-4">
                {record.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    <span className="mr-1 font-mono text-[10px] uppercase tracking-wider text-coffee/70">
                      {t('health.tagsLabel')}
                    </span>
                    {record.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-coffee/80"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                {sourcePaths(record).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    <span className="mr-1 font-mono text-[10px] uppercase tracking-wider text-coffee/70">
                      {t('health.preview.sourceFiles')}
                    </span>
                    {sourcePaths(record).map((path) => (
                      <button
                        key={path}
                        type="button"
                        onClick={() => onPreviewSource(path)}
                        className="rounded-full border border-neutral-200 bg-white px-2 py-0.5 font-mono text-[10px] text-coffee/80 transition hover:border-neutral-300 hover:bg-neutral-50 hover:text-cocoa focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15"
                      >
                        {fileName(path)}
                      </button>
                    ))}
                  </div>
                )}
              </footer>
            )}
          </article>
        </li>
      ))}
    </ol>
  );
}

function groupRecordsByCategory(records: HealthRecord[]): Record<HealthCategoryKey, HealthRecord[]> {
  const grouped: Record<HealthCategoryKey, HealthRecord[]> = {
    blood: [],
    thyroid: [],
    ct: [],
    ultrasound: [],
    endoscopy: [],
    metabolic: [],
    inflammation: [],
    other: [],
  };
  for (const record of records) {
    for (const category of getRecordCategories(record)) {
      grouped[category].push(record);
    }
  }
  return grouped;
}

function getRecordCategories(record: HealthRecord): HealthCategoryKey[] {
  const text = getRecordSearchText(record);
  const categories = new Set<HealthCategoryKey>();

  if (matches(text, ['血常规', '血液', '白细胞', '红细胞', '血红蛋白', '血小板', 'wbc', 'rbc', 'hgb', 'plt', 'cbc'])) {
    categories.add('blood');
  }
  if (matches(text, ['甲状腺', 'tsh', 'tpoab', 'tgab', 'ft3', 'ft4', 'ti-rads', 'thyroid'])) {
    categories.add('thyroid');
  }
  if (matches(text, ['ct', '平扫', '增强'])) {
    categories.add('ct');
  }
  if (matches(text, ['彩超', '彩照', '超声', 'ultrasound', '心超', '颈动脉'])) {
    categories.add('ultrasound');
  }
  if (matches(text, ['胃镜', '肠镜', '内镜', 'endoscopy', '结肠', '胃炎', '腺瘤'])) {
    categories.add('endoscopy');
  }
  if (matches(text, ['甘油三酯', '尿酸', '血脂', '血糖', '脂肪肝', 'hdl', 'ldl', 'triglycerides', 'uric acid', 'metabolic'])) {
    categories.add('metabolic');
  }
  if (matches(text, ['crp', 'c反应蛋白', '炎症', '中性粒', '白细胞升高'])) {
    categories.add('inflammation');
  }

  if (categories.size === 0) categories.add('other');
  return [...categories];
}

function getRecordSearchText(record: HealthRecord): string {
  return [
    record.kind,
    record.provider,
    record.department,
    record.title.en,
    record.title.zh,
    record.summary.en,
    record.summary.zh,
    record.notes,
    ...record.tags,
    ...record.findings.flatMap((finding) => [finding.label.en, finding.label.zh, finding.value, finding.unit, finding.referenceRange]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function matches(text: string, keywords: string[]): boolean {
  return keywords.some((keyword) => text.includes(keyword.toLowerCase()));
}

function sourcePaths(record: HealthRecord): string[] {
  return [
    ...(record.sourceImagePaths ?? []),
    ...(record.sourceImagePath ? [record.sourceImagePath] : []),
  ];
}

function fileName(path: string): string {
  return path.split('/').pop() ?? path;
}

function getDateRange(records: HealthRecord[], formatter: Intl.DateTimeFormat, fallback: string): string {
  if (records.length === 0) return fallback;
  const sorted = [...records].sort((a, b) => recordTime(a) - recordTime(b));
  return `${formatRecordDate(sorted[0], formatter, fallback)} – ${formatRecordDate(sorted[sorted.length - 1], formatter, fallback)}`;
}

function recordTime(record: HealthRecord): number {
  const value = Date.parse(record.occurredAt ?? record.parsedAt);
  return Number.isNaN(value) ? 0 : value;
}

function formatRecordDate(
  record: HealthRecord,
  formatter: Intl.DateTimeFormat,
  fallback: string,
): string {
  return formatDate(record.occurredAt ?? record.parsedAt, formatter, fallback);
}

function formatDate(value: string | undefined, formatter: Intl.DateTimeFormat, fallback: string): string {
  if (!value) return fallback;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? fallback : formatter.format(date);
}
